import { computed, ref } from 'vue';

import { en } from './messages/en.ts';
import { th } from './messages/th.ts';

/**
 * Interface language.
 *
 * Hand-rolled rather than `vue-i18n`, for the same reason the router matcher
 * and the rate limiter are: what this app needs is a lookup, an
 * interpolation and a plural rule, and the platform already provides the only
 * hard one. `Intl.PluralRules` knows the categories for every language there
 * is, so a hundred lines here is *more* correct than a hand-written rule
 * table would be, and costs nothing in the bundle.
 *
 * The locale follows `users.language` — the same value that decides which
 * language messages are translated into. One setting, one meaning: the
 * language you read in.
 */

/** Catalogues we have actually authored. Anything else falls back to English. */
export const MESSAGES = { en, th } as const;

export type Locale = keyof typeof MESSAGES;
export type MessageKey = keyof typeof en;

export const SUPPORTED_LOCALES = Object.keys(MESSAGES) as Locale[];

/**
 * Right-to-left scripts.
 *
 * None of them have a catalogue yet, so nothing selects them today — but the
 * document direction is set from this rather than assumed, so adding Arabic is
 * a file and not a hunt through the layout for hard-coded `left`.
 */
const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ur']);

const current = ref<Locale>('en');

/** The language actually chosen, even when we have no catalogue for it. */
const requested = ref<string>('en');

export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as string[]).includes(value);
}

/**
 * Picks the closest catalogue we have.
 *
 * `zh-TW` falls back to `zh` before it falls back to English, so a regional
 * variant never skips straight past its own language.
 */
export function resolveLocale(language: string | null | undefined): Locale {
  if (!language) return 'en';
  const lower = language.toLowerCase();
  if (isSupportedLocale(lower)) return lower;

  const base = lower.split('-')[0] ?? '';
  if (isSupportedLocale(base)) return base;
  return 'en';
}

export function setLocale(language: string | null | undefined): void {
  requested.value = language ?? 'en';
  current.value = resolveLocale(language);

  if (typeof document !== 'undefined') {
    // Screen readers announce in the wrong accent without this, and browsers
    // pick the wrong hyphenation and font stack.
    document.documentElement.lang = requested.value;
    document.documentElement.dir = RTL_LANGUAGES.has(current.value) ? 'rtl' : 'ltr';
  }
}

/**
 * The language a signed-out visitor picked for themselves.
 *
 * Without this the switcher in the header held only until the next full page
 * load, when `main.ts` read the browser again and undid it — so somebody who
 * chose ไทย on the landing page arrived at /register back in English. It also
 * gives the country suggestion somewhere to record that it was answered:
 * accepting stores the new language, declining stores the current one, and
 * either way the suggestion has been settled and is not asked again.
 *
 * Signed in, the account's own `language` wins over this — that is a setting
 * the person saved deliberately, and it follows them between devices.
 *
 * `localStorage` throws outright in some privacy modes rather than returning
 * null, so every access is guarded. Losing the preference is a smaller
 * failure than failing to boot.
 */
const STORAGE_KEY = 'talkvibe.locale';

export function storedLocale(): string | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value !== null && value.length > 0 && value.length <= 20 ? value : null;
  } catch {
    return null;
  }
}

export function rememberLocale(language: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // A preference that cannot be written still applies to this page.
  }
}

export function forgetLocale(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to undo */
  }
}

/** Sets the language *and* records that the choice was the visitor's own. */
export function chooseLocale(language: string): void {
  rememberLocale(language);
  setLocale(language);
}

export const locale = computed(() => current.value);
/** For `Intl`, which understands far more languages than we have catalogues. */
export const intlLocale = computed(() => requested.value);

type Params = Record<string, string | number>;

/**
 * Looks up a message and fills in its placeholders.
 *
 * A missing key returns the key itself rather than an empty string: a screen
 * reading `chat.send` is obviously broken and gets fixed, while a blank button
 * looks like a styling problem and survives for months.
 */
export function translate(key: MessageKey | string, params?: Params): string {
  return translateIn(current.value, key, params);
}

/**
 * The same lookup, in a named catalogue instead of the current one.
 *
 * For the one case where writing in the language on screen is the wrong
 * choice: offering somebody a language they can read, on a page they
 * currently cannot. An invitation to switch to Thai, written in English, is
 * addressed to exactly the person who will not understand it.
 */
export function translateIn(target: Locale, key: MessageKey | string, params?: Params): string {
  const table = MESSAGES[target] as Record<string, string>;
  const template = table[key] ?? (en as Record<string, string>)[key];
  if (template === undefined) return key;
  return interpolate(template, params);
}

/**
 * The plural form for `count`, chosen by the language's own rules.
 *
 * Keys are `<key>.one`, `<key>.other` and so on. Thai has only `other`;
 * English has `one` and `other`; Arabic has six. Asking `Intl` which one
 * applies is the difference between working in every language and working in
 * the two we happened to test.
 */
export function plural(key: string, count: number, params?: Params): string {
  const table = MESSAGES[current.value] as Record<string, string>;
  const fallback = en as Record<string, string>;

  let category: string;
  try {
    category = new Intl.PluralRules(requested.value).select(count);
  } catch {
    category = count === 1 ? 'one' : 'other';
  }

  const template =
    table[`${key}.${category}`] ??
    table[`${key}.other`] ??
    fallback[`${key}.${category}`] ??
    fallback[`${key}.other`];
  if (template === undefined) return key;

  return interpolate(template, { ...params, count: formatNumber(count) });
}

function interpolate(template: string, params?: Params): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
}

/** Thai and Arabic digits, thousands separators — all of it is locale-specific. */
export function formatNumber(value: number): string {
  try {
    return new Intl.NumberFormat(requested.value).format(value);
  } catch {
    return String(value);
  }
}

export function useI18n() {
  return { t: translate, plural, locale, intlLocale, formatNumber, setLocale, chooseLocale };
}

/**
 * The visitor's own language, for anyone not signed in.
 *
 * `navigator.languages` is ordered by preference, so the first entry we have
 * a catalogue for wins — a browser set to `["th-TH", "en-US"]` gets Thai, not
 * whichever we happen to check first.
 */
export function browserLanguage(): string {
  if (typeof navigator === 'undefined') return 'en';
  for (const candidate of navigator.languages ?? [navigator.language]) {
    if (!candidate) continue;
    const resolved = resolveLocale(candidate);
    if (resolved !== 'en' || candidate.toLowerCase().startsWith('en')) return candidate;
  }
  return 'en';
}
